import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Pencil, Trash2, Plus, Eye, EyeOff } from "lucide-react";

export default function AdminDriverManager() {
  const [drivers, setDrivers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    status: "active",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/drivers`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch drivers");
        const data = await res.json();
        setDrivers(data);
      } catch (err) {
        setError("Could not load drivers.");
        console.error(err);
      }
    };

    fetchDrivers();
  }, [BASE_URL]);

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", password: "", status: "active" });
    setEditIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");

    try {
      const payload = { ...form };
      if (editIndex !== null && !payload.password) {
        delete payload.password;
      }

      const response = editIndex !== null
        ? await fetch(`${BASE_URL}/api/admin/drivers/${drivers[editIndex]._id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          })
        : await fetch(`${BASE_URL}/api/admin/drivers`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "An error occurred");
      }

      const updatedDriver = await response.json();
      console.log("Updated driver response:", updatedDriver);

      if (!updatedDriver._id) {
        throw new Error("Updated driver missing _id field");
      }

      let updatedDrivers;

      if (editIndex !== null) {
        updatedDrivers = drivers.map((driver) =>
          driver._id === updatedDriver._id ? updatedDriver : driver
        );
        setSuccess("Driver updated successfully.");
      } else {
        updatedDrivers = [...drivers, updatedDriver];
        setSuccess("Driver added successfully.");
      }

      setDrivers(updatedDrivers);
      resetForm();
      if (editIndex === null) setFormOpen(false);

      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  const handleEdit = (index) => {
    setFormOpen(true);
    setForm(drivers[index]);
    setEditIndex(index);
    setSuccess("");
    setError("");
  };

  const handleDelete = async (index) => {
    const confirmed = window.confirm("Are you sure you want to remove this driver?");
    if (!confirmed) return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/drivers/${drivers[index]._id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete driver");

      const updated = [...drivers];
      updated.splice(index, 1);
      setDrivers(updated);
      setSuccess("Driver deleted successfully.");
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError("Error deleting driver.");
      console.error(err);
    }
  };


  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-hidden">
      {success && (
        <div className="bg-green-100 text-green-700 px-4 py-2 rounded mb-4">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-100 text-red-600 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}
      {/* Header */}
      <div
        className="flex justify-between items-center bg-gray-100 border rounded px-4 py-3 cursor-pointer mb-6"
        onClick={() => {
          setFormOpen(!formOpen);
          if (!formOpen) resetForm();
        }}
      >
        <h2 className="text-xl font-semibold">
          {editIndex !== null ? "Edit Driver" : "Add New Driver"}
        </h2>
        {formOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {/* Form */}
      {formOpen && (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow mb-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Driver Name <span className="text-red-600">*</span>
              </label>
              <input
                required
                type="text"
                className="border p-2 rounded w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Phone Number <span className="text-red-600">*</span>
              </label>
              <input
                required
                type="tel"
                className="border p-2 rounded w-full"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                required
                type="email"
                className="border p-2 rounded w-full"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                Password {editIndex === null && <span className="text-red-600">*</span>}
              </label>
              <input
                required={editIndex === null}
                type={showPassword ? "text" : "password"}
                className="border p-2 rounded w-full pr-10"
                value={form.password || ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <span
                className="absolute top-9 right-3 cursor-pointer text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Status <span className="text-red-600">*</span>
              </label>
              <select
                className="border p-2 rounded w-full"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Pending</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              className="flex items-center justify-center bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-500 w-full sm:w-auto"
            >
              {editIndex !== null ? "Update Driver" : <span className="flex items-center gap-2"><Plus /> Add Driver</span>}
            </button>

            {editIndex !== null && (
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setFormOpen(false);
                }}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded hover:bg-gray-400 w-full sm:w-auto"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {/* Table */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm text-left table-auto">
          <thead className="bg-gray-100 text-gray-700">
            <tr className="whitespace-nowrap">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {drivers.map((driver, index) => (
              <tr key={index} className="whitespace-nowrap">
                <td className="px-3 py-2">{driver.name}</td>
                <td className="px-3 py-2">{driver.phone}</td>
                <td className="px-3 py-2">{driver.email}</td>
                <td className="px-3 py-2 capitalize">{driver.status}</td>
                <td className="px-3 py-2 flex gap-2 flex-wrap align-middle">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-blue-600 hover:underline mr-2 flex items-center gap-1"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:underline flex items-center gap-1"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {drivers.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-4 text-gray-500 italic text-center">
                  No drivers added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
